import _ from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { ThemeProvider } from 'styled-components';

import {
    Icon,
    IconClose,
    IconParticipants
} from '../../base/icons';
import {
    PARTICIPANT_ROLE,
    getParticipants,
    isLocalParticipantModerator
} from '../../base/participants';
import { getBrandingColorPalette } from '../../base/styles';
import { AddBreakoutRoomButton } from '../../breakout-rooms/components/web/AddBreakoutRoomButton';
import { RoomList } from '../../breakout-rooms/components/web/RoomList';
import {

    // getBreakoutRooms,
    // getCurrentRoomId,
    isAddBreakoutRoomButtonVisible
} from '../../breakout-rooms/functions';
import { FocusModeSection } from '../../focus-mode';
import { LobbySection } from '../../lobby';
import { getLobbyState } from '../../lobby/functions';
import { getVisitorsRoster, isVisitorsModeEnabled } from '../../visitors/functions';
import { amIInBackstage, amIWebinarModerator, getMainRoomRoster, isBackstageModeEnabled } from '../../webinar';
import { close } from '../actions';
import {
    findStyledAncestor,
    getParticipantsPaneOpen
} from '../functions';
import theme from '../theme.json';

import { InviteButton } from './InviteButton';
import { LobbyParticipantList } from './LobbyParticipantList';
import MainRoomParticipantList from './MainRoomParticipantList';
import { MeetingParticipantList } from './MeetingParticipantList';
import VisitorsList from './VisitorsList';
import VisitorsRoster from './VisitorsRoster';
import { useModerationStyles } from './moderation/styles';
import {
    AntiCollapse,
    ColoredIcon,
    Container,
    FooterEllipsisContainer,
    Header
} from './styled';

declare var interfaceConfig: any;

export const ParticipantsPane = props => {
    const dispatch = useDispatch();
    const { classes, cx } = useModerationStyles();
    const paneOpen = useSelector(getParticipantsPaneOpen);
    const [ fMute, setFmute ] = useState(false);
    const [ fMuteVideo, setFMuteVideo ] = useState(false);
    const [ contextOpen, setContextOpen ] = useState(false);
    const visitorsCount = useSelector(state => state['features/visitors'].count || 0);
    const { knockingParticipants } = useSelector(getLobbyState);
    const { t } = useTranslation();
    const lockaudio = useSelector(
    state => state['features/base/conference']?.lockAudio
    );
    const lockAudioString = JSON.stringify(lockaudio);
    const lockvideo = useSelector(
    state => state['features/base/conference']?.lockVideo
    );
    const lockVideoString = JSON.stringify(lockvideo);
    const toolbarButtons = useSelector(
    state => state['features/base/config'].interfaceConfig?.TOOLBAR_BUTTONS
    );
    const visibleButtons = new Set(toolbarButtons);
    const partipicipantss = useSelector(
    state => state['features/base/participants']
    );
    const showAddBreakoutRoom = useSelector(isAddBreakoutRoomButtonVisible);
    const participants = useSelector(getParticipants, _.isEqual);

    // Backstage is a separate room, so the list above it is the backstage
    // occupancy. A moderator running the meeting from back there still needs to
    // see and moderate the people who are live, which is what the second tab is.
    const inBackstage = useSelector(amIInBackstage);
    const isWebinarModerator = useSelector(amIWebinarModerator);
    const backstageEnabled = useSelector(isBackstageModeEnabled);
    const mainRoomRoster = useSelector(getMainRoomRoster);
    const [ mainRoomTabActive, setMainRoomTabActive ] = useState(false);

    // A moderator standing backstage always gets the tabs, feature switch or
    // not: without them they could neither see nor moderate the live room.
    const showRoomTabs = inBackstage && isWebinarModerator && (backstageEnabled || mainRoomRoster.length > 0);
    const showMainRoom = showRoomTabs && mainRoomTabActive;

    const showBackstageTab = useCallback(() => setMainRoomTabActive(false), []);
    const showMainRoomTab = useCallback(() => setMainRoomTabActive(true), []);

    useEffect(() => {
        if (!showRoomTabs) {
            setMainRoomTabActive(false);
        }
    }, [ showRoomTabs ]);

    // The viewers tab lists everyone sitting on the visitor nodes: the main
    // roster never sees them, so this tab is the only place a moderator can
    // watch and act on them. It stays hidden while the backstage tabs are up
    // (three tabs deep) or while the room reports nothing visitor-related.
    const visitorsRoster = useSelector(getVisitorsRoster);
    const visitorsModeEnabled = useSelector(isVisitorsModeEnabled);
    const isLocalModerator = useSelector(isLocalParticipantModerator);
    const showViewersTab = !showRoomTabs && isLocalModerator
        && (visitorsModeEnabled || visitorsCount > 0 || visitorsRoster.length > 0);
    const [ viewersTabActive, setViewersTabActive ] = useState(false);

    const showParticipantsTab = useCallback(() => setViewersTabActive(false), []);
    const showViewersTabContent = useCallback(() => setViewersTabActive(true), []);

    useEffect(() => {
        if (!showViewersTab) {
            setViewersTabActive(false);
        }
    }, [ showViewersTab ]);

    useEffect(() => {
        const areParticipantsNotModerators = partipicipantss.filter(
      e => e.role !== PARTICIPANT_ROLE.MODERATOR
        );
        const fMutedorNot = areParticipantsNotModerators.every(
      i => lockaudio[`${i.id}`] !== true
        );

        setFmute(fMutedorNot);
    }, [ lockAudioString, partipicipantss ]);

    useEffect(() => {
        const areParticipantsNotModerators = partipicipantss.filter(
      e => e.role !== PARTICIPANT_ROLE.MODERATOR
        );

        const fMutedorNot = areParticipantsNotModerators.every(
      i => lockvideo[`${i.id}`] !== true
        );

        setFMuteVideo(fMutedorNot);
    }, [ lockVideoString, partipicipantss ]);

    const closePane = () => {
        setContextOpen(false);
        dispatch(close());
    };

    const closePaneKeyPress = e => {
        if (e.key === 'Enter' || e.key === ' ') {
            closePane();
        }
    };

    useEffect(() => {
        const handler = [
            'click',
            e => {
                if (!findStyledAncestor(e.target, FooterEllipsisContainer)) {
                    setContextOpen(false);
                }
            }
        ];

        window.addEventListener(...handler);

        return () => window.removeEventListener(...handler);
    }, [ contextOpen ]);

    const participantPaneStyle = {
        padding: paneOpen ? '0px 10px 0px 0px' : '0px'
    };

    const participantPaneHeaderStyle = { display: 'flex',
        alignItems: 'center',
        gap: '10px' };
    const margin = {
        marginTop: '10px'
    };

    return (
        <ThemeProvider theme = { theme }>
            <div
                className = { 'participants_pane' }
                style = { participantPaneStyle }>
                <div
                    className = 'participants_pane-content'>
                    <Header theme = {{ headerBackground: getBrandingColorPalette(props.interfaceConfig)?.primary }}>
                        <div className = 'participant-pane-heading'>
                            <div
                                style = { participantPaneHeaderStyle }>
                                <ColoredIcon color = { interfaceConfig.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig).accent1 : '#ffffff' }>
                                    <Icon
                                        className = 'participant_pane-header-icon'
                                        color = { interfaceConfig.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig).accent1 : '#ffffff' }
                                        fillColor = { interfaceConfig.applyMeetingSettings ? getBrandingColorPalette(interfaceConfig).accent1 : '#ffffff' }
                                        size = { 27 }
                                        src = { IconParticipants } />
                                </ColoredIcon>
                                <div className = 'participants_page_heading'>
                                    {visibleButtons.has('participant-count') ? t('participantsPane.headings.participantsList', {
                                        count: participants.length
                                    }) : t('participantsPane.headings.participantsList')}
                                </div>
                            </div>
                            <div
                                style = { participantPaneHeaderStyle }>
                                {visibleButtons.has('invite')
                                && <InviteButton
                                    textToDisplay = { t('addPeople.add') }
                                    tooltip = { false } />}
                                <Icon
                                    ariaLabel = { t('toolbar.closeChat') }
                                    onClick = { closePane }
                                    onKeyPress = { closePaneKeyPress }
                                    role = 'button'
                                    src = { IconClose }
                                    tabIndex = { 0 } />
                            </div>
                        </div>
                    </Header>
                    <Container>
                        {showRoomTabs && (
                            <div
                                className = { classes.tabs }
                                role = 'tablist'>
                                <button
                                    aria-selected = { !mainRoomTabActive }
                                    className = { cx(classes.tab, !mainRoomTabActive && classes.tabActive) }
                                    onClick = { showBackstageTab }
                                    role = 'tab'
                                    type = 'button'>
                                    {t('webinar.backstageTab')}
                                </button>
                                <button
                                    aria-selected = { mainRoomTabActive }
                                    className = { cx(classes.tab, mainRoomTabActive && classes.tabActive) }
                                    onClick = { showMainRoomTab }
                                    role = 'tab'
                                    type = 'button'>
                                    {t('webinar.mainRoomLive', { count: mainRoomRoster.length })}
                                </button>
                            </div>
                        )}
                        {showViewersTab && (
                            <div
                                className = { classes.tabs }
                                role = 'tablist'>
                                <button
                                    aria-selected = { !viewersTabActive }
                                    className = { cx(classes.tab, !viewersTabActive && classes.tabActive) }
                                    onClick = { showParticipantsTab }
                                    role = 'tab'
                                    type = 'button'>
                                    {t('participantsPane.header')}
                                </button>
                                <button
                                    aria-selected = { viewersTabActive }
                                    className = { cx(classes.tab, viewersTabActive && classes.tabActive) }
                                    onClick = { showViewersTabContent }
                                    role = 'tab'
                                    type = 'button'>
                                    {t('participantsPane.headings.visitors', {
                                        count: visitorsRoster.length || visitorsCount })}
                                </button>
                            </div>
                        )}
                        {showMainRoom ? <MainRoomParticipantList /> : viewersTabActive ? (
                            <>
                                <VisitorsList />
                                <VisitorsRoster />
                            </>
                        ) : (
                            <>
                                <LobbySection />
                                <FocusModeSection />
                                {!showViewersTab && visitorsCount > 0 && <VisitorsList />}
                                {knockingParticipants.length > 0 && (
                                    <div style = { margin }>
                                        <LobbyParticipantList />
                                    </div>
                                )}
                                <MeetingParticipantList
                                    fMute = { fMute }
                                    fMuteVideo = { fMuteVideo } />
                                <RoomList />
                                <div className = 'breakoutroom-parent'>
                                    {showAddBreakoutRoom && <AddBreakoutRoomButton />}
                                </div>
                            </>
                        )}
                        <AntiCollapse />
                    </Container>
                </div>
            </div>
        </ThemeProvider>
    );
};
